import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Edit, Trash2, GripVertical, Plus } from 'lucide-react';
import Button from '@/components/ui/Button';
import { Country } from '@/types/model';
import { useTranslation, usePermissions, useCountries, useDeleteCountry, useConfirmDialog } from '@/hooks';
import { countryKeys } from '@/hooks/useCountries';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { countryService } from '@/services/countryService';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

const CountriesTable = ({
  countries,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  isReordering,
  onDragStart,
  onDragEnd,
  activeId,
}: {
  countries: Country[];
  onEdit: (country: Country) => void;
  onDelete: (country: Country) => void;
  canEdit: boolean;
  canDelete: boolean;
  isReordering: boolean;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => Promise<void>;
  activeId: string | null;
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );
  const { t } = useTranslation();

  const renderFlag = (country: Country) => {
    if (!country.flag) {
      return (
        <span className="flex h-10 w-10 items-center justify-center bg-gray-100 text-sm font-semibold text-gray-500">
          {(country.nameEnglish || country.name).slice(0, 2).toUpperCase()}
        </span>
      );
    }

    if (/^https?:\/\//.test(country.flag)) {
      return (
        <img
          src={country.flag}
          alt={country.nameEnglish || country.name}
          className="h-10 w-15 object-cover"
        />
      );
    }

    return (
      <span className="text-3xl" role="img" aria-label={country.nameEnglish || country.name}>
        {country.flag}
      </span>
    );
  };

  const SortableRow = ({ country }: { country: Country }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: country.id });
    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <tr ref={setNodeRef} style={style} className="hover:bg-gray-50">
        <td className="px-3 py-4 text-gray-400 cursor-grab align-middle w-12" {...attributes} {...listeners}>
          <GripVertical size={18} />
        </td>
        <td className="px-4 py-4 text-sm text-gray-900 align-middle w-20">
          {typeof country.displayOrder === 'number' ? country.displayOrder : '—'}
        </td>
        <td className="px-6 py-4 align-middle">
          <div className="flex items-center gap-4">
            {renderFlag(country)}
            <div>
              <div className="text-base font-semibold text-gray-900">{country.name}</div>
            </div>
          </div>
        </td>
        <td className="px-6 py-4 w-40 align-middle">
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => onEdit(country)}>
                <Edit size={14} />
              </Button>
            )}
            {canDelete && (
              <Button size="sm" variant="danger" onClick={() => onDelete(country)} disabled={isReordering}>
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const activeCountry = countries.find((country) => country.id === activeId) || null;

  return (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12" />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                {t.countries.fields.displayOrder}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t.countries.fields.name}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                {t.common.actions}
              </th>
            </tr>
          </thead>
          <SortableContext items={countries.map((country) => country.id)} strategy={verticalListSortingStrategy}>
            <tbody className="bg-white divide-y divide-gray-200">
              {countries.map((country) => (
                <SortableRow key={country.id} country={country} />
              ))}
            </tbody>
          </SortableContext>
        </table>
        <DragOverlay>
          {activeCountry ? (
            <div className="flex items-center gap-3 bg-white shadow rounded px-3 py-2 border">
              <GripVertical size={16} className="text-gray-400" />
              <span className="text-sm text-gray-800 font-medium truncate max-w-[240px]">
                {activeCountry.nameEnglish}
              </span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      {countries.length === 0 && (
        <div className="px-6 py-8 text-center text-gray-500">
          {t.countries.messages.noData}
        </div>
      )}
    </div>
  );
};

const Countries = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { data, isLoading, isError, error, refetch } = useCountries();
  const deleteCountryMutation = useDeleteCountry();
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirmDialog();

  const [countries, setCountries] = useState<Country[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);

  useEffect(() => {
    if (data) {
      const ordered = [...data].sort((a, b) => {
        const orderA = typeof a.displayOrder === 'number' ? a.displayOrder : Number.MAX_SAFE_INTEGER;
        const orderB = typeof b.displayOrder === 'number' ? b.displayOrder : Number.MAX_SAFE_INTEGER;
        return orderA - orderB || a.nameEnglish.localeCompare(b.nameEnglish);
      });
      setCountries(ordered);
    }
  }, [data]);

  const canCreate = hasPermission('TVCHANNEL_CREATE');
  const canEdit = hasPermission('TVCHANNEL_UPDATE');
  const canDelete = hasPermission('TVCHANNEL_DELETE');

  const orderedCountries = useMemo(() => countries, [countries]);

  const handleCreate = () => {
    navigate('/content/countries/create');
  };

  const handleEdit = (country: Country) => {
    navigate(`/content/countries/${country.id}/edit`);
  };

  const handleDelete = async (country: Country) => {
    try {
      const confirmed = await confirm({
        title: t.common.confirmationTitle,
        message: t.countries.messages.confirmDelete,
        confirmLabel: t.buttons.delete,
        cancelLabel: t.buttons.cancel,
        confirmVariant: 'danger',
        onConfirm: async () => {
          await deleteCountryMutation.mutateAsync(country.id);
        },
      });

      if (!confirmed) {
        return;
      }
      toast.success(t.countries.messages.deleted);
    } catch (err) {
      console.error('Delete country error', err);
      toast.error(err instanceof Error ? err.message : t.errors.unexpectedError);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const current = [...orderedCountries];
    const previousOrders = new Map(current.map((country) => [country.id, country.displayOrder]));
    const oldIndex = current.findIndex((country) => country.id === active.id);
    const newIndex = current.findIndex((country) => country.id === over.id);

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const movedList = arrayMove(current, oldIndex, newIndex).map((country, index) => ({
      ...country,
      displayOrder: index + 1,
    }));
    const updates = movedList.filter((country) => previousOrders.get(country.id) !== country.displayOrder);

    setCountries(movedList);
    setIsReordering(true);

    try {
      if (updates.length > 0) {
        await Promise.all(
          updates.map((country) =>
            country.displayOrder
              ? countryService.updateCountryDisplayOrder(country.id, country.displayOrder)
              : Promise.resolve()
          )
        );
      }
      await queryClient.invalidateQueries({ queryKey: countryKeys.list() });
      await queryClient.invalidateQueries({ queryKey: countryKeys.dropdown() });
    } catch (err) {
      console.error('Error updating display order', err);
      await refetch();
    } finally {
      setIsReordering(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">
              {error instanceof Error ? error.message : t.errors.loadingError}
            </p>
            <div className="mt-4 flex gap-2">
              <Button variant="outline" onClick={() => refetch()}>
                {t.buttons.retry}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800">{t.countries.listTitle}</h2>
            <p className="text-gray-500">{t.countries.description}</p>
          </div>
          {canCreate && (
            <Button onClick={handleCreate} className="flex items-center gap-2">
              <Plus size={16} />
              {t.countries.actions.create}
            </Button>
          )}
        </div>

        <CountriesTable
          countries={orderedCountries}
          onEdit={handleEdit}
          onDelete={handleDelete}
          canEdit={canEdit}
          canDelete={canDelete}
          isReordering={isReordering}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          activeId={activeId}
        />
        {dialog}
      </div>
    </div>
  );
};

const ProtectedCountries = () => (
  <ProtectedRoute anyPermission={['TVCHANNEL_READ', 'TVCHANNEL_CREATE', 'TVCHANNEL_UPDATE', 'TVCHANNEL_DELETE']}>
    <Countries />
  </ProtectedRoute>
);

export default ProtectedCountries;
